// ============================================================================
// Géométrie déductive du noyau (lot 5.4) : la DÉDUCTION en un pas.
// ============================================================================
// La base : les propositions et les relations entre longueurs connues (énoncé +
// lignes validées), les alignements qu'elles imposent (« J ∈ [AC] » : (AJ) = (AC)),
// et les longueurs connues. Une ligne est justifiée si UNE règle du paquet, dont
// toutes les conditions sont dans la base, la donne. Les motifs sont unifiés aux
// faits (points variables injectifs, droites à alignement près).
// ============================================================================

import type { Term } from '../term';
import { evalFloat } from '../normal';
import { cycleKey, gpropText, type GProp, type LineRef, type Pt } from './props';
import {
  knownValues,
  parseMetric,
  sameRelation,
  substRel,
  constTruth,
  type MLine,
  type MRel,
} from './metric';
import type { GeoRule, Pattern } from './rules';

export interface GeoFact {
  text: string;
  g?: GProp;
  m?: MLine;
}

interface LineSet {
  id: string;
  pts: Set<Pt>;
  names: Set<string>;
}
/** proposition aux droites remplacées par leur identifiant (alignements compris) */
type CProp =
  | { k: 'par' | 'npar' | 'perp'; a: string; b: string }
  | { k: 'mid' | 'onseg' | 'bis' | 'circ'; p: Pt; a: Pt; b: Pt }
  | { k: 'online'; p: Pt; l: string }
  | { k: 'para' | 'rectangle' | 'losange' | 'carre'; v: Pt[] }
  | { k: 'rt' | 'iso'; at: Pt; b: Pt; c: Pt }
  | { k: 'nrt'; v: Pt[] };

export class GeoBase {
  readonly points: ReadonlySet<string>;
  readonly lines: LineSet[] = [];
  readonly props: CProp[];
  readonly rels: MRel[];
  readonly V: Map<string, Term>;
  private metricInstances: { rule: GeoRule; rel: MRel }[] | null = null;

  constructor(
    facts: GeoFact[],
    points: ReadonlySet<string>,
    readonly rules: readonly GeoRule[],
  ) {
    this.points = points;
    this.buildLines(facts.flatMap((f) => (f.g ? [f.g] : [])));
    this.props = facts.flatMap((f) => (f.g ? [this.canon(f.g)] : []));
    this.rels = facts.flatMap((f) => f.m?.rels ?? []);
    this.V = knownValues(this.rels);
  }

  /* ------------------------------ alignements ------------------------------ */

  private buildLines(props: GProp[]): void {
    const seeds: { pts: Pt[]; names: string[] }[] = [];
    const ref = (l: LineRef): void => {
      seeds.push(l.k === 'pts' ? { pts: [l.a, l.b], names: [] } : { pts: [], names: [l.n] });
    };
    for (const p of props) {
      if (p.k === 'mid' || p.k === 'onseg') seeds.push({ pts: [p.a, p.b, p.p], names: [] });
      else if (p.k === 'online')
        seeds.push(
          p.l.k === 'pts'
            ? { pts: [p.l.a, p.l.b, p.p], names: [] }
            : { pts: [p.p], names: [p.l.n] },
        );
      else if (p.k === 'par' || p.k === 'npar' || p.k === 'perp') {
        ref(p.a);
        ref(p.b);
      }
    }
    const sets = seeds.map((s) => ({ pts: new Set(s.pts), names: new Set(s.names) }));
    let merged = true;
    while (merged) {
      merged = false;
      outer: for (let i = 0; i < sets.length; i++)
        for (let j = i + 1; j < sets.length; j++) {
          const a = sets[i]!;
          const b = sets[j]!;
          const common = [...a.pts].filter((x) => b.pts.has(x)).length;
          const named = [...a.names].some((n) => b.names.has(n));
          if (common >= 2 || named) {
            b.pts.forEach((x) => a.pts.add(x));
            b.names.forEach((x) => a.names.add(x));
            sets.splice(j, 1);
            merged = true;
            break outer;
          }
        }
    }
    sets.forEach((s, i) => this.lines.push({ id: `L${i}`, pts: s.pts, names: s.names }));
  }
  /** identifiant de la droite (créée si elle n'est dans aucun alignement connu) */
  lineId(l: LineRef): string {
    const found = this.lines.find((s) =>
      l.k === 'pts' ? s.pts.has(l.a) && s.pts.has(l.b) : s.names.has(l.n),
    );
    if (found) return found.id;
    const id = `L${this.lines.length}`;
    this.lines.push({
      id,
      pts: new Set(l.k === 'pts' ? [l.a, l.b] : []),
      names: new Set(l.k === 'name' ? [l.n] : []),
    });
    return id;
  }
  private line(id: string): LineSet {
    return this.lines.find((s) => s.id === id)!;
  }

  canon(p: GProp): CProp {
    switch (p.k) {
      case 'par':
      case 'npar':
      case 'perp':
        return { k: p.k, a: this.lineId(p.a), b: this.lineId(p.b) };
      case 'online':
        return { k: 'online', p: p.p, l: this.lineId(p.l) };
      default:
        return p as CProp;
    }
  }

  /* ------------------------------ ce qu'on sait déjà ------------------------------ */

  /** La proposition est-elle déjà dans la base (à alignement et à l'ordre près) ? */
  established(p: GProp): boolean {
    if (p.k === 'online') {
      const id = this.lineId(p.l);
      return this.line(id).pts.has(p.p);
    }
    const c = this.canon(p);
    if ((c.k === 'par' || c.k === 'npar' || c.k === 'perp') && c.a === c.b) return false;
    const key = cKey(c);
    return this.props.some(
      (f) =>
        cKey(f) === key || (c.k === 'onseg' && f.k === 'mid' && cKey({ ...f, k: 'onseg' }) === key),
    );
  }
  /** même proposition, à l'ordre et aux alignements connus près */
  sameProp(a: GProp, b: GProp): boolean {
    if (a.k === 'online' || b.k === 'online')
      return (
        a.k === 'online' && b.k === 'online' && a.p === b.p && this.lineId(a.l) === this.lineId(b.l)
      );
    return cKey(this.canon(a)) === cKey(this.canon(b));
  }
  /** les deux droites sont-elles la même droite (alignement connu) ? */
  sameLine(a: LineRef, b: LineRef): boolean {
    return this.lineId(a) === this.lineId(b);
  }

  /** Relation déjà connue telle quelle (sans remplacer les longueurs) ? */
  knownRel(r: MRel): MRel | null {
    return this.rels.find((f) => sameRelation(f, r)) ?? null;
  }

  /* ------------------------------ unification ------------------------------ */

  private bindPt(b: Binding, v: Pt, x: Pt): Binding | null {
    if (!v.startsWith('?')) return v === x ? b : null;
    const cur = b.get(v);
    if (cur !== undefined) return cur === x ? b : null;
    for (const [k, y] of b) if (y === x && !k.startsWith('?l')) return null;
    const nb = new Map(b);
    nb.set(v, x);
    return nb;
  }
  private bindLine(b: Binding, pat: LineRef, id: string): Binding[] {
    if (pat.k === 'name') {
      if (!pat.n.startsWith('?')) return this.lineId(pat) === id ? [b] : [];
      const key = '?l' + pat.n;
      const cur = b.get(key);
      if (cur !== undefined) return cur === id ? [b] : [];
      for (const [k, y] of b) if (k.startsWith('?l') && y === id) return [];
      const nb = new Map(b);
      nb.set(key, id);
      return [nb];
    }
    const pts = [...this.line(id).pts];
    const out: Binding[] = [];
    for (const x of pts)
      for (const y of pts) {
        if (x === y) continue;
        const b1 = this.bindPt(b, pat.a, x);
        const b2 = b1 && this.bindPt(b1, pat.b, y);
        if (b2) out.push(b2);
      }
    return out;
  }
  private bindPts(b: Binding | null, vs: Pt[], xs: Pt[]): Binding | null {
    let cur = b;
    for (let i = 0; i < vs.length && cur; i++) cur = this.bindPt(cur, vs[i]!, xs[i]!);
    return cur;
  }

  /** extensions de β qui rendent le motif `pat` égal au fait canonique `f` */
  private unify(pat: GProp, f: CProp, b: Binding): Binding[] {
    const kOk = pat.k === f.k || (pat.k === 'onseg' && f.k === 'mid');
    if (!kOk) return [];
    switch (pat.k) {
      case 'par':
      case 'npar':
      case 'perp': {
        const g = f as { a: string; b: string };
        const out: Binding[] = [];
        for (const [x, y] of [
          [g.a, g.b],
          [g.b, g.a],
        ] as [string, string][])
          for (const b1 of this.bindLine(b, pat.a, x)) out.push(...this.bindLine(b1, pat.b, y));
        return out;
      }
      case 'mid':
      case 'onseg':
      case 'bis':
      case 'circ': {
        const g = f as { p: Pt; a: Pt; b: Pt };
        return [
          this.bindPts(b, [pat.p, pat.a, pat.b], [g.p, g.a, g.b]),
          this.bindPts(b, [pat.p, pat.a, pat.b], [g.p, g.b, g.a]),
        ].filter((x): x is Binding => !!x);
      }
      case 'rt':
      case 'iso': {
        const g = f as { at: Pt; b: Pt; c: Pt };
        return [
          this.bindPts(b, [pat.at, pat.b, pat.c], [g.at, g.b, g.c]),
          this.bindPts(b, [pat.at, pat.b, pat.c], [g.at, g.c, g.b]),
        ].filter((x): x is Binding => !!x);
      }
      case 'nrt': {
        const v = (f as { v: Pt[] }).v;
        return permutations(v)
          .map((p) => this.bindPts(b, pat.v, p))
          .filter((x): x is Binding => !!x);
      }
      case 'online':
        return [];
      default: {
        const v = (f as { v: Pt[] }).v;
        return dihedral(v)
          .map((p) => this.bindPts(b, (pat as { v: Pt[] }).v, p))
          .filter((x): x is Binding => !!x);
      }
    }
  }

  /** « ?M ∈ (?A?B) » : M est sur la droite qui porte A et B (alignements connus) */
  private unifyOnline(pat: Extract<GProp, { k: 'online' }>, b: Binding): Binding[] {
    const out: Binding[] = [];
    for (const L of this.lines) {
      if (pat.l.k === 'name') {
        for (const p of L.pts) {
          const b1 = this.bindPt(b, pat.p, p);
          if (b1) out.push(...this.bindLine(b1, pat.l, L.id));
        }
        continue;
      }
      const pts = [...L.pts];
      if (pts.length < 3) continue;
      for (const p of pts)
        for (const x of pts)
          for (const y of pts) {
            if (p === x || p === y || x === y) continue;
            const b1 = this.bindPts(b, [pat.p, pat.l.a, pat.l.b], [p, x, y]);
            if (b1) out.push(b1);
          }
    }
    return out;
  }

  private metricHolds(text: string, b: Binding): boolean {
    const m = parseMetric(instantiate(text, b), this.points);
    if (!m.ok) return false;
    return m.m.rels.every((r) => this.rels.some((f) => sameRelation(f, r)));
  }

  /** toutes les façons de satisfaire les conditions, à partir de β */
  satisfy(hyps: Pattern[], b: Binding, i = 0, out: Binding[] = [], cap = 400): Binding[] {
    if (out.length >= cap) return out;
    if (i === hyps.length) {
      out.push(b);
      return out;
    }
    const h = hyps[i]!;
    if (h.k === 'metric') {
      const free = [...new Set(h.text.match(/\?[A-Z][0-9]*/g) ?? [])].filter((v) => !b.has(v));
      for (const b1 of this.assignFree(free, b))
        if (this.metricHolds(h.text, b1)) this.satisfy(hyps, b1, i + 1, out, cap);
      return out;
    }
    const exts =
      h.p.k === 'online'
        ? this.unifyOnline(h.p, b)
        : this.props.flatMap((f) => this.unify(h.p, f, b));
    for (const b1 of exts) this.satisfy(hyps, b1, i + 1, out, cap);
    return out;
  }
  private assignFree(free: string[], b: Binding): Binding[] {
    if (!free.length) return [b];
    const [v, ...rest] = free;
    const out: Binding[] = [];
    for (const p of this.points) {
      const b1 = this.bindPt(b, v!, p);
      if (b1) out.push(...this.assignFree(rest, b1));
    }
    return out;
  }

  private conditionsHold(rule: GeoRule, b: Binding): boolean {
    for (const w of rule.when) {
      const [s, ...others] = w.longest.map((t) => instantiate(t, b));
      const val = (seg: string): number | null => {
        const k = seg.length === 2 && seg[0]! > seg[1]! ? seg[1]! + seg[0]! : seg;
        const t = this.V.get(k);
        return t ? evalFloat(t, {}) : null;
      };
      const vs = val(s!);
      if (vs === null) return false;
      for (const o of others) {
        const vo = val(o);
        if (vo === null || vo > vs + 1e-9) return false;
      }
    }
    return true;
  }

  /* ------------------------------ justification ------------------------------ */

  /** La règle qui donne la proposition en un pas (null : aucune). */
  justifyProp(p: GProp): GeoRule | null {
    const target = this.canon(p);
    for (const rule of this.rules)
      for (const c of rule.concl) {
        if (c.k !== 'prop') continue;
        for (const b of this.unify(c.p, target, new Map()))
          for (const b1 of this.satisfy(rule.hyps, b))
            if (this.conditionsHold(rule, b1)) return rule;
      }
    return null;
  }

  /** Les relations entre longueurs que donnent les règles (conclusions instanciées). */
  private instances(): { rule: GeoRule; rel: MRel }[] {
    if (this.metricInstances) return this.metricInstances;
    const out: { rule: GeoRule; rel: MRel }[] = [];
    for (const rule of this.rules) {
      const concl = rule.concl.filter((c) => c.k === 'metric');
      if (!concl.length) continue;
      for (const b of this.satisfy(rule.hyps, new Map())) {
        if (!this.conditionsHold(rule, b)) continue;
        for (const c of concl) {
          const m = parseMetric(instantiate(c.text, b), this.points);
          if (m.ok) m.m.rels.forEach((rel) => out.push({ rule, rel }));
        }
      }
    }
    this.metricInstances = out;
    return out;
  }
  /**
   * La règle qui donne la relation : telle quelle, ou une fois les longueurs connues
   * remplacées (« IJ = 4 » quand BC = 8 et I, J milieux).
   */
  justifyRel(r: MRel): GeoRule | null {
    const rv = substRel(r, this.V);
    const rvConst = constTruth(rv) !== null;
    for (const { rule, rel } of this.instances()) {
      if (sameRelation(rel, r)) return rule;
      if (!rvConst && sameRelation(substRel(rel, this.V), rv)) return rule;
    }
    return null;
  }
}

export type Binding = Map<string, string>;

/** « ?A?M ÷ ?A?B » → « AM ÷ AB » */
export function instantiate(text: string, b: Binding): string {
  return text.replace(/\?[A-Z][0-9]*/g, (v) => b.get(v) ?? v);
}

function cKey(c: CProp): string {
  switch (c.k) {
    case 'par':
    case 'npar':
    case 'perp': {
      const [x, y] = [c.a, c.b].sort();
      return `${c.k} ${x} ${y}`;
    }
    case 'mid':
    case 'onseg':
    case 'bis':
    case 'circ':
      return `${c.k} ${c.p} ${[c.a, c.b].sort().join('')}`;
    case 'online':
      return `online ${c.p} ${c.l}`;
    case 'rt':
    case 'iso':
      return `${c.k} ${c.at} ${[c.b, c.c].sort().join('')}`;
    case 'nrt':
      return `nrt ${[...c.v].sort().join('')}`;
    default:
      return `${c.k} ${cycleKey(c.v)}`;
  }
}
function permutations<T>(xs: T[]): T[][] {
  if (xs.length <= 1) return [xs];
  return xs.flatMap((x, i) =>
    permutations([...xs.slice(0, i), ...xs.slice(i + 1)]).map((p) => [x, ...p]),
  );
}
function dihedral(v: Pt[]): Pt[][] {
  const out: Pt[][] = [];
  for (let i = 0; i < v.length; i++) {
    const r = [...v.slice(i), ...v.slice(0, i)];
    out.push(r, [...r].reverse());
  }
  return out;
}

export { gpropText };
